/**
 * Cost Breakdown Pie Chart
 *
 * Displays cost distribution by type (agent execution, API calls, storage, etc.)
 * Uses Recharts library for visualizations
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { ProjectCostByType } from '../../api/projects';

export interface CostBreakdownPieChartProps {
  data: ProjectCostByType[];
  isLoading?: boolean;
}

// Color palette for cost types
const COLORS: Record<string, string> = {
  agent_execution: '#3b82f6', // blue
  api_calls: '#10b981',        // green
  storage: '#f59e0b',          // amber
  compute: '#8b5cf6',          // purple
  other: '#6b7280'             // gray
};

const LABELS: Record<string, string> = {
  agent_execution: 'Agent Execution',
  api_calls: 'API Calls',
  storage: 'Storage',
  compute: 'Compute',
  other: 'Other'
};

export const CostBreakdownPieChart: React.FC<CostBreakdownPieChartProps> = ({
  data,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-400">Loading chart data...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500">No cost data available</p>
          <p className="text-sm text-gray-400 mt-1">Costs will appear as they are incurred</p>
        </div>
      </div>
    );
  }

  // Transform data for chart
  const chartData = data.map(item => ({
    name: LABELS[item.Cost_Type] || item.Cost_Type,
    value: parseFloat(item.Total_Amount.toString()),
    cost_type: item.Cost_Type,
    event_count: item.Event_Count
  }));

  // Custom label for pie chart
  const renderLabel = (entry: any) => {
    const percent = ((entry.value / chartData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1);
    return `${entry.name}: ${percent}%`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">
            Cost: ${data.value.toFixed(2)}
          </p>
          <p className="text-sm text-gray-600">
            Events: {data.event_count.toLocaleString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderLabel}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={COLORS[entry.cost_type] || COLORS.other}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="bottom"
          height={36}
          formatter={(value: string) => (
            <span className="text-sm text-gray-700">{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

export default CostBreakdownPieChart;
